import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { XCircle, Home, CreditCard, AlertCircle } from 'lucide-react';

const FailedContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 120px 20px 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FailedCard = styled.div`
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  border-top: 4px solid #ef4444;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: shake 0.5s ease-out;

  @keyframes shake {
    0%, 100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-10px);
    }
    75% {
      transform: translateX(10px);
    }
  }
`;

const Title = styled.h1`
  color: #1e293b;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  color: #64748b;
  font-size: 1.125rem;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
`;

const ErrorTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #991b1b;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ErrorText = styled.p`
  color: #7f1d1d;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 4px 0;
`;

const DetailBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #7F1B1C 0%, #640003 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #640003 0%, #4a0002 100%);
    box-shadow: 0 10px 25px rgba(127, 27, 28, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: #7F1B1C;
  border: 2px solid #7F1B1C;

  &:hover {
    background: #7F1B1C;
    color: white;
    box-shadow: 0 10px 20px rgba(127, 27, 28, 0.2);
  }
`;

const HelpText = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #fef2f2;
  border-left: 4px solid #7F1B1C;
  border-radius: 8px;
  text-align: left;
`;

const HelpTitle = styled.div`
  color: #7F1B1C;
  font-weight: 600;
  margin-bottom: 8px;
`;

const HelpContent = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 4px 0;
`;

const KPCardRechargeFailed = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    // Parse transaction details from URL parameters
    const details = {
      reference: searchParams.get('REFERENCE') || 'N/A',
      amount: searchParams.get('TXTPRICE') || searchParams.get('AMOUNT') || 'N/A',
      currency: searchParams.get('CURRENCY') || 'NAD',
      transactionId: searchParams.get('TRANSACTIONID') || 'N/A',
      cardNumber: searchParams.get('VARIABLE1') || 'N/A',
      errorCode: searchParams.get('ERRORCODE') || 'UNKNOWN',
      errorMessage: searchParams.get('ERRORMESSAGE') || 'Transaction failed. Please try again.',
      date: new Date().toLocaleString(),
      status: 'Failed'
    };
    setTransactionDetails(details);
  }, [searchParams]);

  const getErrorDescription = (errorCode) => {
    const errorDescriptions = {
      'DECLINED': 'Your card was declined by the bank. Please check your card details and try again.',
      'INSUFFICIENT_FUNDS': 'Insufficient funds in your account. Please ensure you have enough balance.',
      'INVALID_CARD': 'Invalid card details provided. Please verify your card information.',
      'EXPIRED_CARD': 'Your card has expired. Please use a different card.',
      '3DSECURE_FAILED': '3D Secure authentication failed. Please verify with your bank.',
      'TIMEOUT': 'Transaction timed out. Please try again.',
      'UNKNOWN': 'An unknown error occurred. Please contact support if the issue persists.'
    };
    return errorDescriptions[errorCode] || errorDescriptions['UNKNOWN'];
  };

  return (
    <FailedContainer>
      <FailedCard>
        <IconContainer>
          <XCircle size={48} color="white" />
        </IconContainer>

        <Title>Payment Failed</Title>
        <Message>
          Unfortunately, your payment could not be processed. No charges have been made to your account.
        </Message>

        {transactionDetails && (
          <>
            <ErrorBox>
              <ErrorTitle>
                <AlertCircle size={20} />
                Error Details
              </ErrorTitle>
              <ErrorText>
                <strong>Error Code:</strong> {transactionDetails.errorCode}
              </ErrorText>
              <ErrorText>
                {getErrorDescription(transactionDetails.errorCode)}
              </ErrorText>
            </ErrorBox>

            <DetailBox>
              <DetailRow>
                <DetailLabel>Reference</DetailLabel>
                <DetailValue>{transactionDetails.reference}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>KP Card Number</DetailLabel>
                <DetailValue>{transactionDetails.cardNumber}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Attempted Amount</DetailLabel>
                <DetailValue>
                  {transactionDetails.currency} {transactionDetails.amount}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status</DetailLabel>
                <DetailValue style={{ color: '#ef4444' }}>
                  {transactionDetails.status}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Date & Time</DetailLabel>
                <DetailValue>{transactionDetails.date}</DetailValue>
              </DetailRow>
            </DetailBox>
          </>
        )}

        <ButtonGroup>
          <PrimaryButton onClick={() => navigate('/kp-card/recharge')}>
            <CreditCard size={20} />
            Try Again
          </PrimaryButton>
          <SecondaryButton onClick={() => navigate('/')}>
            <Home size={20} />
            Go to Home
          </SecondaryButton>
        </ButtonGroup>

        <HelpText>
          <HelpTitle>Need Help?</HelpTitle>
          <HelpContent>
            If you continue to experience issues, please contact our support team:
          </HelpContent>
          <HelpContent>
            <strong>Phone:</strong> +264 857 208 426
          </HelpContent>
          <HelpContent>
            <strong>Email:</strong> support@keyplotparking.com
          </HelpContent>
        </HelpText>
      </FailedCard>
    </FailedContainer>
  );
};

export default KPCardRechargeFailed;

